import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cx from 'classnames'
import prettyBytes from 'pretty-bytes'

import Alert from 'common/components/alert/Alert'
import Button from 'common/components/button'
import Loader from 'common/components/loaders/Loader'
import { CloseSmall, Email } from 'common/icons'

const MAX_EML_SIZE_BYTES = 15728640 // 15MB — .eml text can be bulky with inline base64 images

export type EmlSelection = {
  file: File
  subject: string | null
  html: string | null
  isProcessing: boolean
  error: string | null
}

type Props = {
  selection: EmlSelection | null
  onFileSelected: (file: File) => void
  onRemoveFile: () => void
}

const EmlImportTabContent = ({
  selection,
  onFileSelected,
  onRemoveFile,
}: Props) => {
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const isBusy = !!selection?.isProcessing

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'message/rfc822': ['.eml'] },
    multiple: false,
    maxSize: MAX_EML_SIZE_BYTES,
    noClick: isBusy,
    noDrag: isBusy,
    noKeyboard: isBusy,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setRejectionError(
          fileRejections[0].errors[0]?.message ??
            'This file could not be accepted.'
        )
        return
      }
      setRejectionError(null)
      const file = acceptedFiles[0]
      if (file) {
        onFileSelected(file)
      }
    },
  })

  const displayError = rejectionError ?? selection?.error ?? null

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {displayError && (
        <Alert type="error" showIcon className="mb-4 shrink-0">
          {displayError}
        </Alert>
      )}
      <div
        {...getRootProps()}
        data-testid="eml-upload-dropzone"
        className={cx(
          'flex-1 min-h-0 flex flex-col items-center justify-center gap-4 rounded-sm px-6 py-8 outline-none transition-colors bg-gold-300/25',
          {
            'cursor-pointer': !isBusy,
            'cursor-not-allowed': isBusy,
          }
        )}
      >
        <input {...getInputProps()} />
        {selection ? (
          <>
            {selection.isProcessing ? (
              <Loader size="small" />
            ) : selection.html ? (
              <div className="flex-1 min-h-0 w-full flex flex-col gap-2">
                {selection.subject && (
                  <p
                    className="text-sm text-base-700 shrink-0"
                    data-testid="eml-subject"
                  >
                    <span className="font-medium">Subject:</span>{' '}
                    {selection.subject}
                  </p>
                )}
                <div className="flex-1 min-h-0 grid">
                  <iframe
                    srcDoc={selection.html}
                    className="h-full w-full rounded-md border-2 border-gold-400 shadow-md bg-white"
                    sandbox=""
                    title="Email preview"
                    data-testid="eml-preview"
                  />
                </div>
              </div>
            ) : (
              <Email
                size={12}
                isDefaultColor={false}
                className="text-coolGray-400"
              />
            )}
            <div className="flex items-center gap-2 text-base-700 shrink-0 bg-white rounded-full border border-gold-400 shadow-sm px-4 py-2">
              <span className="text-sm font-medium" data-testid="eml-file-name">
                {selection.file.name} ({prettyBytes(selection.file.size)})
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveFile()
                }}
                data-testid="eml-file-remove-button"
              >
                <CloseSmall size={3} isDefaultColor={false} />
              </button>
            </div>
          </>
        ) : (
          <>
            <Email
              size={12}
              isDefaultColor={false}
              className="text-coolGray-400"
            />
            <div className="text-center">
              <p className="text-2xl font-relais text-base-700">
                {isDragActive
                  ? 'Drop your file here'
                  : 'Drag & drop your file here'}
              </p>
              <p className="text-sm text-base-500 pt-2">
                Supports .eml files up to 15MB
              </p>
            </div>
            <Button
              variant="secondary"
              size="medium"
              data-testid="eml-browse-button"
            >
              Browse files
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default EmlImportTabContent
